/**
 * AudioSource Behavior
 *
 * Routes audio from a sibling source behavior (Video or AudioClip) through the
 * Web Audio API for spatial playback. Models Unity's AudioSource: master
 * volume, distance-based rolloff, and a 2D↔3D spatial blend.
 *
 * Pairing: looks for a Video or AudioClip behavior on the same SceneObject.
 * If found, captures the element's audio via MediaElementAudioSourceNode and
 * routes it through:
 *
 *   media element → input gain (volume) → split
 *                                       ├→ dry path (gain = 1 − spatialBlend)
 *                                       └→ panner (3D) → wet gain (spatialBlend)
 *                                       └→ destination
 *
 * The PannerNode position follows the SceneObject's world transform; the
 * AudioListener follows the active camera (see AudioManager).
 */

import { Observable, Observer } from "@babylonjs/core/Misc/observable";
import { Scene } from "@babylonjs/core/scene";
import { TraceBehavior } from "./tracebehavior";
import { AudioSourceData, AudioRolloffMode } from "../scenedata/behaviors/audiosourcedata";
import { ValidationError } from "../scenedata/behaviordata";
import { Video } from "./video";
import { AudioClip } from "./audioclip";
import type { AudioManager } from "../managers/AudioManager";
import { Logger } from "../utils/Logger";

/**
 * Structural interface satisfied by Video and AudioClip: a behavior that owns
 * an HTMLMediaElement and exposes an observable that fires when it's ready.
 */
interface MediaElementHost {
    getMediaElement(): HTMLMediaElement | null;
    onMediaElementReadyObservable: Observable<HTMLMediaElement>;
}

/** Track which media elements already have a MediaElementAudioSourceNode bound. */
const mediaElementSources = new WeakMap<HTMLMediaElement, MediaElementAudioSourceNode>();

export class AudioSource extends TraceBehavior<AudioSourceData> {
    static readonly behaviorType = "AudioSource" as const;

    private _mediaSource: MediaElementAudioSourceNode | null = null;
    private _inputGain: GainNode | null = null;
    private _dryGain: GainNode | null = null;
    private _wetGain: GainNode | null = null;
    private _panner: PannerNode | null = null;

    /** Sibling host (Video or AudioClip) whose element we route. */
    private _host: MediaElementHost | null = null;
    private _hostReadyObserver: Observer<HTMLMediaElement> | null = null;
    /** Waits for a host to be added later; set only while there is none. */
    private _siblingObserver: Observer<TraceBehavior> | null = null;
    private _positionObserver: Observer<Scene> | null = null;
    private _lastPosX = 0;
    private _lastPosY = 0;
    private _lastPosZ = 0;

    protected validate(data: AudioSourceData): ValidationError[] {
        const errors: ValidationError[] = [];
        if (data.volume !== undefined && (data.volume < 0 || data.volume > 1)) {
            errors.push({ field: 'volume', message: 'Volume must be 0–1' });
        }
        if (data.minDistance !== undefined && data.minDistance < 0) {
            errors.push({ field: 'minDistance', message: 'Min distance must be >= 0' });
        }
        if (data.maxDistance !== undefined && data.maxDistance < 0) {
            errors.push({ field: 'maxDistance', message: 'Max distance must be >= 0' });
        }
        if (
            data.minDistance !== undefined && data.maxDistance !== undefined &&
            data.minDistance > data.maxDistance
        ) {
            errors.push({ field: 'maxDistance', message: 'maxDistance must be >= minDistance' });
        }
        if (data.spatialBlend !== undefined && (data.spatialBlend < 0 || data.spatialBlend > 1)) {
            errors.push({ field: 'spatialBlend', message: 'spatialBlend must be 0–1' });
        }
        return errors;
    }

    protected onAttach(): void {
        // The scene loaders register every behavior before any of them
        // attaches, so a Video or AudioClip sibling is found here whatever
        // order the record was saved in, and an implied host (appended after
        // the authored behaviors) counts too. Only a host added to a live
        // object after this AudioSource (`addBehavior`) is missing; wait for it.
        const host = this._findHost();
        if (host) {
            this._bindHost(host);
            return;
        }
        Logger.debug(`[AudioSource] No Video or AudioClip sibling on "${this.sceneObject.name}" yet; waiting for one.`);
        this._siblingObserver = this.sceneObject.onBehaviorAddedObservable.add((added) => {
            if (!(added instanceof Video || added instanceof AudioClip)) return;
            this._stopWatchingSiblings();
            this._bindHost(added as MediaElementHost);
        });
    }

    protected onDetach(): void {
        this._stopWatchingSiblings();
        if (this._hostReadyObserver && this._host) {
            this._host.onMediaElementReadyObservable.remove(this._hostReadyObserver);
            this._hostReadyObserver = null;
        }
        this._removePositionObserver();
        this._teardownGraph();
        this._host = null;
    }

    /**
     * Prefer Video so existing scenes keep working unchanged. AudioClip is the
     * standalone-clip path. Observable types differ between the two hosts
     * (HTMLVideoElement vs HTMLAudioElement) but both extend HTMLMediaElement,
     * which is all this behavior needs.
     */
    private _findHost(): MediaElementHost | null {
        const video = this.sceneObject.getBehavior(Video);
        const clip = video ? null : this.sceneObject.getBehavior(AudioClip);
        return (video ?? clip) as MediaElementHost | null;
    }

    private _stopWatchingSiblings(): void {
        if (!this._siblingObserver) return;
        this.sceneObject.onBehaviorAddedObservable.remove(this._siblingObserver);
        this._siblingObserver = null;
    }

    private _bindHost(host: MediaElementHost): void {
        this._host = host;

        // Hook now if the element is already ready; otherwise wait for the observable.
        const existing = host.getMediaElement();
        if (existing) {
            this._hookMediaElement(existing);
        } else {
            this._hostReadyObserver = host.onMediaElementReadyObservable.add((el) => {
                this._hookMediaElement(el);
            });
        }
    }

    protected async applyCommit(): Promise<void> {
        // Push current resolved values to the audio graph. Safe to call when nodes are null
        // (e.g. before the video element is ready) — values get re-applied in _hookMediaElement.
        this._applyParams();
    }

    private get _audioManager(): AudioManager {
        return this.sceneObject.traceScene.getAudioManager();
    }

    /**
     * Build the Web Audio graph for the given media element (HTMLVideoElement
     * or HTMLAudioElement). MediaElementAudioSourceNode is one-per-element —
     * we use a WeakMap to share the source node if the same element gets bound
     * twice (e.g. behavior toggle).
     */
    private _hookMediaElement(el: HTMLMediaElement): void {
        // Tear down any prior graph for this AudioSource before rebuilding. The
        // observable fires once per load today, but a media reload (URL change,
        // future hot-reload) would re-fire and stack duplicate gain/panner nodes.
        if (this._inputGain) {
            this._removePositionObserver();
            this._teardownGraph();
        }

        const mgr = this._audioManager;
        const ctx = mgr.getContext();

        // Reuse a previously-created source for this element, if any. Creating a
        // second MediaElementAudioSourceNode on the same element throws.
        let source = mediaElementSources.get(el);
        if (!source) {
            try {
                source = ctx.createMediaElementSource(el);
                mediaElementSources.set(el, source);
            } catch (e) {
                Logger.warn(`[AudioSource] Failed to create media element source: ${e instanceof Error ? e.message : String(e)}`);
                return;
            }
        } else {
            // Cached source from a prior AudioSource lifecycle on this element.
            // _teardownGraph() reconnected it directly to the destination as a
            // fallback; disconnect everything so the new graph below is the
            // only audio path.
            try { source.disconnect(); } catch { /* not connected */ }
        }
        this._mediaSource = source;

        // input gain (master volume)
        const input = ctx.createGain();

        // dry path (2D): bypasses the panner
        const dry = ctx.createGain();

        // wet path (3D): through the panner
        const panner = ctx.createPanner();
        panner.panningModel = 'HRTF';
        const wet = ctx.createGain();

        // Initial panner position from current world transform
        const pos = this._emitterPosition();
        const now = ctx.currentTime;
        panner.positionX.setValueAtTime(pos.x, now);
        panner.positionY.setValueAtTime(pos.y, now);
        panner.positionZ.setValueAtTime(pos.z, now);
        this._lastPosX = pos.x; this._lastPosY = pos.y; this._lastPosZ = pos.z;

        // Wire: source → input → (dry → dest) and (panner → wet → dest)
        source.connect(input);
        input.connect(dry);
        input.connect(panner);
        panner.connect(wet);
        dry.connect(mgr.getDestination());
        wet.connect(mgr.getDestination());

        this._inputGain = input;
        this._dryGain = dry;
        this._wetGain = wet;
        this._panner = panner;

        this._applyParams();
        this._ensurePositionObserver();
    }

    /** Push current resolved values to the live audio nodes. */
    private _applyParams(): void {
        // Skip when the graph isn't built yet — applyCommit() fires before the
        // media element is ready. _hookMediaElement() calls us again after
        // wiring the nodes, so no values are lost. The four graph nodes are
        // created and torn down as a set, so checking _inputGain is sufficient.
        if (!this._inputGain) return;
        const ctx = this._audioManager.getContext();

        const volume = this._resolved.volume ?? 1;
        const spatialBlend = Math.max(0, Math.min(1, this._resolved.spatialBlend ?? 1));
        const minDistance = this._resolved.minDistance ?? 1;
        const maxDistance = Math.max(minDistance, this._resolved.maxDistance ?? 10);
        const rolloff: AudioRolloffMode = this._resolved.volumeRollOff ?? "Linear";

        const now = ctx.currentTime;

        this._inputGain.gain.setValueAtTime(volume, now);
        this._dryGain!.gain.setValueAtTime(1 - spatialBlend, now);
        this._wetGain!.gain.setValueAtTime(spatialBlend, now);
        const panner = this._panner!;
        panner.distanceModel = rolloff === "Logarithmic" ? 'inverse' : 'linear';
        panner.refDistance = minDistance;
        panner.maxDistance = maxDistance;
        // PannerNode's rolloffFactor controls steepness. 1 is the default and
        // matches Unity's reference curves closely enough for both models.
        panner.rolloffFactor = 1;
    }

    /**
     * Where the sound comes from: the object's EFFECTIVE position — through
     * its animation root when it has one, so a clip that offsets the object
     * (a `TRACE_animation_binding` root channel, M_authored · M_root(t); an
     * AnimationSequence) carries the emitter with it. A recording's voice
     * follows the performer's head this way.
     */
    private _emitterPosition() {
        return (this.sceneObject.animationRoot ?? this.sceneObject.transform).getAbsolutePosition();
    }

    private _ensurePositionObserver(): void {
        if (this._positionObserver || !this.scene || !this._panner) return;

        this._positionObserver = this.scene.onBeforeRenderObservable.add(() => {
            if (!this._panner) return;
            const pos = this._emitterPosition();
            const dx = pos.x - this._lastPosX;
            const dy = pos.y - this._lastPosY;
            const dz = pos.z - this._lastPosZ;
            if (dx * dx + dy * dy + dz * dz < 1e-6) return;
            this._lastPosX = pos.x;
            this._lastPosY = pos.y;
            this._lastPosZ = pos.z;
            this._panner.positionX.value = pos.x;
            this._panner.positionY.value = pos.y;
            this._panner.positionZ.value = pos.z;
        });
    }

    private _removePositionObserver(): void {
        if (this._positionObserver && this.scene) {
            this.scene.onBeforeRenderObservable.remove(this._positionObserver);
            this._positionObserver = null;
        }
    }

    /**
     * Disconnect our gain/panner nodes from the graph. We intentionally do NOT
     * dispose the MediaElementAudioSourceNode — it's cached per element in the
     * module-level WeakMap because a second createMediaElementSource on the
     * same element throws. Once an element is routed through Web Audio it can
     * never go back to the default sink, so we reconnect the source directly
     * to the destination so the media keeps audible playback after the
     * AudioSource is removed.
     */
    private _teardownGraph(): void {
        try { this._mediaSource?.disconnect(); } catch { /* not connected */ }
        try { this._inputGain?.disconnect(); } catch { /* noop */ }
        try { this._dryGain?.disconnect(); } catch { /* noop */ }
        try { this._wetGain?.disconnect(); } catch { /* noop */ }
        try { this._panner?.disconnect(); } catch { /* noop */ }
        // Restore unspatialized playback so removing AudioSource doesn't mute the video.
        if (this._mediaSource) {
            try { this._mediaSource.connect(this._audioManager.getDestination()); } catch { /* noop */ }
        }
        this._inputGain = null;
        this._dryGain = null;
        this._wetGain = null;
        this._panner = null;
        this._mediaSource = null;
    }
}
